import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Minus, FilePlus } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import UploadQueueItem from './UploadQueueItem';
import {
  subscribe, addFiles, startProcessing, clearQueue,
  retryItem, skipItem, snapshotState, CONFIG,
} from '../../lib/uploads';

export default function UploadReceiptsModal({ onClose, onMinimize, onReceiptCreated }) {
  const { colors } = useTheme();
  const [state, setState] = useState(() => snapshotState());
  const [dragging, setDragging] = useState(false);
  const [rejectedToast, setRejectedToast] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => subscribe(setState), []);
  // Dismiss the rejected toast after 4s
  useEffect(() => {
    if (!rejectedToast) return undefined;
    const t = setTimeout(() => setRejectedToast(null), 4000);
    return () => clearTimeout(t);
  }, [rejectedToast]);

  const handleFiles = async (fileList) => {
    const res = await addFiles(fileList);
    if (res.rejected.length > 0) {
      setRejectedToast(res.rejected);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) await handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); if (!dragging) setDragging(true); };
  const onDragLeave = (e) => {
    // Only clear when leaving the drop zone, not its children
    if (e.currentTarget === e.target) setDragging(false);
  };

  const startAll = () => startProcessing({ onReceiptCreated });

  const canStart = state.queued > 0 && !state.running;
  const canClose = !state.running; // if running, Close is replaced by Minimize

  const progressPct = state.total === 0
    ? 0
    : Math.round(((state.done + state.failed + state.skipped) / state.total) * 100);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={() => canClose && onClose?.()}
      data-testid="upload-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        data-testid="upload-modal"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: colors.border }}
        >
          <div className="flex items-center gap-2">
            <Upload size={18} style={{ color: colors.accent }} />
            <h2 className="text-base font-semibold" style={{ color: colors.text }}>
              {state.running
                ? `Processing ${state.done + state.failed + state.skipped + 1 > state.total
                    ? state.total
                    : state.done + state.failed + state.skipped + 1} of ${state.total}`
                : 'Upload Receipts'}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            {state.running ? (
              <button
                data-testid="upload-minimize"
                onClick={() => onMinimize?.()}
                className="p-1 rounded hover:opacity-70"
                style={{ color: colors.textMuted }}
                aria-label="Minimize"
                title="Minimize — processing will keep running"
              >
                <Minus size={18} />
              </button>
            ) : null}
            <button
              data-testid="upload-close"
              onClick={() => canClose && onClose?.()}
              className="p-1 rounded hover:opacity-70 disabled:opacity-30"
              style={{ color: colors.textMuted }}
              disabled={!canClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Progress bar (visible once processing starts) */}
        {state.total > 0 ? (
          <div className="h-1 w-full" style={{ backgroundColor: colors.border }}>
            <div
              className="h-full transition-all"
              data-testid="upload-progress"
              style={{ width: `${progressPct}%`, backgroundColor: colors.accent }}
            />
          </div>
        ) : null}

        {/* Body */}
        <div className="px-5 py-4 flex-1 overflow-y-auto">
          {/* Drop zone */}
          <div
            data-testid="upload-dropzone"
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); }
            }}
            role="button"
            tabIndex={0}
            className="rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors"
            style={{
              borderColor: dragging ? colors.accent : colors.border,
              backgroundColor: dragging ? colors.accentSoft : 'transparent',
            }}
          >
            <FilePlus size={28} className="mx-auto mb-2" style={{ color: colors.accent }} />
            <div className="text-sm font-semibold" style={{ color: colors.text }}>
              Drag JPG, PNG, or PDF files here
            </div>
            <div className="text-xs mt-1" style={{ color: colors.textMuted }}>
              or click to choose from your computer · up to {CONFIG.MAX_FILES} files · 15 MB each · first page of each PDF
            </div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,application/pdf"
              style={{ display: 'none' }}
              data-testid="upload-file-input"
              onChange={(e) => {
                if (e.target.files?.length) handleFiles(e.target.files);
                e.target.value = ''; // allow same file re-pick
              }}
            />
          </div>

          {/* Rejected toast */}
          {rejectedToast && rejectedToast.length > 0 ? (
            <div
              className="mt-3 rounded-md border px-3 py-2 text-xs"
              style={{ borderColor: '#fca5a5', backgroundColor: '#fee2e2', color: '#991b1b' }}
              data-testid="upload-rejected"
            >
              <div className="font-semibold mb-1">Some files were skipped:</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {rejectedToast.slice(0, 5).map((r, i) => (
                  <li key={i}>
                    <span className="font-medium">{r.name}</span> — {r.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Queue list */}
          {state.items.length > 0 ? (
            <div className="mt-4 space-y-2" data-testid="upload-queue">
              {state.items.map((item) => (
                <UploadQueueItem
                  key={item.id}
                  item={item}
                  colors={colors}
                  onRetry={(id) => retryItem(id, { onReceiptCreated })}
                  onSkip={(id) => skipItem(id)}
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between gap-3 px-5 py-3 border-t"
          style={{ borderColor: colors.border, backgroundColor: colors.surface }}
        >
          <div className="text-xs" style={{ color: colors.textMuted }}>
            {state.total > 0
              ? `${state.done} done · ${state.failed} failed · ${state.skipped} skipped · ${state.queued} queued`
              : `No files yet`}
          </div>
          <div className="flex items-center gap-2">
            {state.allSettled ? (
              <>
                <button
                  data-testid="upload-done-close"
                  onClick={() => { clearQueue(); onClose?.(); }}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white"
                  style={{ backgroundColor: colors.accent }}
                >
                  Done
                </button>
              </>
            ) : (
              <>
                {!state.running && state.total > 0 && state.queued === 0 ? null : (
                  <button
                    data-testid="upload-start"
                    onClick={startAll}
                    disabled={!canStart}
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: colors.accent }}
                  >
                    {state.running ? 'Processing…' : `Start upload${state.queued ? ` (${state.queued})` : ''}`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
